import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import LoadMore from '@/components/ui/LoadMore'
import QueryState from '@/components/ui/QueryState'
import RefreshRegion from '@/components/ui/RefreshRegion'
import { useCourseQuestions } from '@/features/courses/api/queries'
import FaqItem from '@/screens/courses/components/faq/FaqItem'
import { LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'
import LearnHead from '@/screens/courses/components/learn/LearnHead'
import FaqListSkeleton from '@/screens/courses/components/faq/FaqListSkeleton'
import { FAQ_LIST } from '@/screens/courses/components/faq/faq-list-box'
import MoreHelp from '@/screens/courses/components/faq/MoreHelp'
import { FAQ_MAX, FAQ_STEP, faqQuery, loadFaq } from '@/screens/courses/FaqScreen.loader'

const TITLE = 'Course questions'

/**
 * Known without data, so the pending and failed pages draw the real one. This
 * page is not one of the Learn strip's tabs (it is reached from the Courses
 * hub, and keeps the strip with Courses lit: owner L2), so its title shows,
 * with the way back to the hub and the mock's intro line.
 */
const HEADER = (
  <LearnHead
    title={TITLE}
    back="/courses"
    sub="What people ask before they pay for a course. If yours is not here, write to hello@moneymitra.com."
  />
)

function FaqPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading questions…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <FaqListSkeleton />
    </ScreenLayout>
  )
}

/** Light on purpose (docs/route-loading.md, "Error components"). */
function FaqError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} fullWidth header={HEADER} title="Questions did not load" />
}

export const Route = createFileRoute('/_app/courses/faq')({
  loader: loadFaq,
  pendingComponent: FaqPending,
  errorComponent: FaqError,
  component: FaqScreen,
})

/**
 * Learn · Course questions — the questions MoneyMitra answers for every
 * member (mm-be's global FAQ, not one course's), as an accordion, with the
 * first answer open.
 *
 * The list grows by "Load more questions" rather than turning pages: the
 * read is always page 1 at a longer size (`faqQuery`), and `useCourseQuestions`
 * (`keepPreviousData`) keeps the questions on screen while the longer list
 * arrives. A failed read is this list's own failed state (`QueryState` — the
 * placeholder is dropped once the new read errors), not the route's.
 *
 * Any number of answers may be open at once — a member comparing two answers
 * should not lose one to open the other — held here as a set of ids, so an
 * answer stays open as the list grows.
 */
function FaqScreen() {
  const [limit, setLimit] = useState(FAQ_STEP)
  const query = useCourseQuestions(faqQuery(limit))
  const [openIds, setOpenIds] = useState<ReadonlySet<number>>(() => {
    const first = query.data?.rows[0]
    return first === undefined ? new Set() : new Set([first.id])
  })

  const toggle = (id: number) => {
    setOpenIds((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
      <QueryState
        query={query}
        size="page"
        isEmpty={(data) => data.total === 0}
        skeleton={<FaqListSkeleton />}
        loadingLabel="Loading questions…"
        empty={{
          title: 'No questions yet',
          description: 'Answers to the questions members ask most will show here.',
          action: (
            <Link to="/courses" className={stateActionClass('page')}>
              Browse courses
            </Link>
          ),
        }}
        failed={{ title: 'Questions did not load' }}
      >
        {(data) => (
          <div className="flex flex-col">
            <RefreshRegion updating={query.isPlaceholderData} className={FAQ_LIST}>
              {data.rows.map((faq) => (
                <FaqItem
                  key={faq.id}
                  faq={faq}
                  open={openIds.has(faq.id)}
                  onToggle={() => {
                    toggle(faq.id)
                  }}
                />
              ))}
            </RefreshRegion>
            {data.total > FAQ_STEP ? (
              <LoadMore
                shown={data.rows.length}
                total={data.total}
                unit="questions"
                label="Load more questions"
                hasMore={data.rows.length < Math.min(data.total, FAQ_MAX)}
                layout="inline"
                isLoading={query.isPlaceholderData}
                onClick={() => {
                  setLimit(Math.min(data.rows.length + FAQ_STEP, FAQ_MAX))
                }}
              />
            ) : null}
          </div>
        )}
      </QueryState>
      <MoreHelp />
    </ScreenLayout>
  )
}
